import React from "react"
import { useParams } from "react-router-dom"

import { Button, FormControl, Grid, TextField, Typography } from "@material-ui/core"
import { TableColumn } from "@/components/DataTable"
import { Loading, Notification, Portlet } from "@/components"

import { TrackableLinkInteraction, TrackableLinkInteractionResponse } from "@/protocols/trackableLinks"
import { DEFAULT_WHERE_DATA, DefaultWhereDataType } from "@/utils/pagination"
import useValidation, { ErrorType } from "@/hooks/useValidation"

import ApiService from "@/services/Api"
import ErrorHandlerService from "@/services/ErrorHandler"

import TrackableLinkInteractionsSkeleton from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/TrackableLinkInteractions/TrackableLinkInteractionsSkeleton"
import TrackableLinkInteractionChip from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/TrackableLinkInteractions/TrackableLinkInteractionChip"
import TrackableLinkInteractionClient from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/TrackableLinkInteractions/TrackableLinkInteractionClient"

import DataTableWithPagination from "@/components/DataTableWithPagination"

import useStyles from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/TrackableLinkInteractions/styles"

export type InteractionsTableSearchFilters = {
	searchByClientNameOrClientPhoneNumber: string | null
}

export type FormattedInteractionsDataResponse = {
	id: number
	client: TrackableLinkInteraction["client"] | null
	clickedAt: Date
	sentMessageAt: Date | null
}

type FormattedInteractionsData = {
	trackableLinkInteractions: FormattedInteractionsDataResponse[]
	count: number
}

const interactionsTableColumns: TableColumn<FormattedInteractionsDataResponse>[] = [
	{ field: "id", label: "Id da interação" },
	{ field: "client", label: "Nome do cliente", rowRender: (value) => <TrackableLinkInteractionClient client={value as TrackableLinkInteraction["client"] } type="name" /> },
	{ field: "client", label: "Número do cliente", rowRender: (value) => <TrackableLinkInteractionClient client={value as TrackableLinkInteraction["client"] } type="phone" /> },
	{ field: "clickedAt", label: "Clicou no link", rowRender: (value) => <TrackableLinkInteractionChip interactionDate={value as Date} type="click" /> },
	{ field: "sentMessageAt", label: "Enviou mensagem", rowRender: (value) => <TrackableLinkInteractionChip interactionDate={value as Date} type="sent-message" /> }
]

const TrackableLinkInteractions: React.FC = () => {
	const { trackableLinkId } = useParams<{ trackableLinkId?: string }>()
	const { triggerValidation } = useValidation()

	const [interactionsData, setInteractionsData] = React.useState<FormattedInteractionsData>({
		trackableLinkInteractions: [],
		count: 0
	} as FormattedInteractionsData)
	const [loading, setLoading] = React.useState(false)
	const [paginationState, setPaginationState] = React.useState<DefaultWhereDataType>(DEFAULT_WHERE_DATA)
	const [interactionsSearchFilters, setInteractionsSearchFilters] = React.useState<InteractionsTableSearchFilters>({
		searchByClientNameOrClientPhoneNumber: null
	})

	const classes = useStyles()

	const formatInteractionsData = (data: TrackableLinkInteraction[]): FormattedInteractionsDataResponse[] => {
		const formattedData = data.map((interaction) => {
			return {
				id: interaction.id,
				client: interaction.client ? {
					name: interaction.client?.name,
					nickname: interaction.client?.nickname,
					phoneNumber: interaction.client?.phoneNumber
				} : null,
				clickedAt: interaction.clickedAt,
				sentMessageAt: interaction.sentMessageAt
			}
		})

		return formattedData
	}

	const handleGetTrackableLinkInteractions = async () => {
		setLoading(true)
		try {
			const { data } = await ApiService.get<TrackableLinkInteractionResponse>(`/trackable-links/${trackableLinkId}/interactions`, {
				params: {
					search: interactionsSearchFilters.searchByClientNameOrClientPhoneNumber,
					rowsPerPage: paginationState.rowsPerPage,
					page: paginationState.page
				}
			})

			const formattedData = formatInteractionsData(data.trackableLinkInteractions)

			setInteractionsData({
				trackableLinkInteractions: formattedData,
				count: data.count
			})
		} catch (error) {
			Notification.error({ message: "Ocorreu um erro ao buscar as interações" })

			triggerValidation(error as ErrorType)
			ErrorHandlerService.handle(error as ErrorType)
		}

		setLoading(false)
	}

	const handleChangeEditFilters = (data: Partial<InteractionsTableSearchFilters>) => {
		setInteractionsSearchFilters({
			...interactionsSearchFilters,
			...data
		})
	}

	const handlePaginationState = (data: Partial<DefaultWhereDataType>) => {
		setPaginationState(lastState => ({
			...lastState,
			...data
		}))
	}

	const handleApplyFilters = async () => {
		handlePaginationState({ page: DEFAULT_WHERE_DATA.page })
		await handleGetTrackableLinkInteractions()
	}

	React.useEffect(() => {
		handleGetTrackableLinkInteractions()
	}, [paginationState])

	return (
		<Loading
			customLoadingElement={<TrackableLinkInteractionsSkeleton />}
			loading={loading}
		>
			<Grid container direction="column" spacing={3}>
				<Grid item>
					<Typography
						variant="h5"
						className={classes.titleText}
					>
						Interações
					</Typography>
				</Grid>
				<Grid item>
					<Portlet>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<form onSubmit={handleApplyFilters}>
									<Grid container alignItems="center" spacing={1}>
										<Grid item xs={4}>
											<FormControl fullWidth>
												<TextField
													value={interactionsSearchFilters.searchByClientNameOrClientPhoneNumber}
													onChange={
														({ target }) => handleChangeEditFilters({ searchByClientNameOrClientPhoneNumber: target.value?.trimStart() })
													}
													size="medium"
													placeholder="Busque por cliente"
													fullWidth
													variant="outlined"
												/>
											</FormControl>
										</Grid>
										<Grid item>
											<Button
												variant="contained"
												color="primary"
												type="submit"
											>
												Buscar
											</Button>
										</Grid>
									</Grid>
								</form>
							</Grid>

							<Grid item xs={12}>
								<DataTableWithPagination
									columns={interactionsTableColumns}
									data={interactionsData.trackableLinkInteractions}
									page={paginationState.page}
									rowsPerPage={paginationState.rowsPerPage}
									totalRows={interactionsData.count}
									onPaginationChange={handlePaginationState}
								/>
							</Grid>
						</Grid>
					</Portlet>
				</Grid>
			</Grid>
		</Loading>
	)
}

export default TrackableLinkInteractions
